import { getCommunities } from "../services/community.service";
import type { Community, User } from "../types";

export type CommunityMatch = {
  isCommunity: boolean;
  communityId?: string;
  communityName?: string;
  reason?: string;
};

const NAME_ALIASES = [
  "bren northern lights",
  "bren northern light",
  "northern lights bren",
  "bnl apartment",
  "bnl apartments",
];

function normalize(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

/** Ray-casting point-in-polygon (lng/lat as x/y). */
export function isPointInPolygon(
  point: { lat: number; lng: number },
  polygon: { lat: number; lng: number }[]
): boolean {
  if (!polygon || polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    const intersect =
      yi > point.lat !== yj > point.lat &&
      point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi + 0.0) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function addressLooksLikeCommunity(
  address: string | undefined | null,
  community: Community
): boolean {
  if (!address) return false;
  const n = normalize(address);
  const name = normalize(community.name || "");
  const area = normalize(community.area || "");
  const shortId = (community.shortId || "").toLowerCase();

  if (name && n.includes(name)) return true;
  for (const alias of NAME_ALIASES) {
    if (n.includes(alias)) return true;
  }
  // "BNL" as a token (avoid matching random substrings)
  if (shortId && new RegExp(`(^|\\s)${shortId}(\\s|$)`, "i").test(address)) {
    return true;
  }
  // Flat-style: "A-01, Bren Northern Lights..." already covered by name
  if (name && area && n.includes(name.split(" ")[0]) && n.includes("northern")) {
    return true;
  }
  return false;
}

export function matchCommunityForInputs(args: {
  communities: Community[];
  profile?: Partial<User> | null;
  address?: string | null;
  location?: { lat: number; lng: number } | null;
}): CommunityMatch {
  const { communities, profile, address, location } = args;
  const meta = profile?.homeAddressMeta as
    | {
        mode?: string;
        communityId?: string;
        communityName?: string;
        location?: { lat: number; lng: number };
        rawAddressFromMap?: string;
      }
    | undefined;

  // 1) Explicit profile community mode
  if (meta?.mode === "community" && meta.communityId) {
    const c =
      communities.find((x) => x.id === meta.communityId) ||
      communities.find((x) => x.shortId === "BNL");
    return {
      isCommunity: true,
      communityId: meta.communityId,
      communityName: meta.communityName || c?.name,
      reason: "profile_meta",
    };
  }

  const addr =
    address ||
    profile?.homeAddress ||
    profile?.address ||
    meta?.rawAddressFromMap ||
    "";

  const loc = location || meta?.location || null;

  for (const community of communities) {
    if (community.status && community.status !== "active") continue;

    if (addressLooksLikeCommunity(addr, community)) {
      return {
        isCommunity: true,
        communityId: community.id,
        communityName: community.name,
        reason: "address_text",
      };
    }

    if (loc && community.geoFenceCoords && community.geoFenceCoords.length >= 3) {
      if (isPointInPolygon(loc, community.geoFenceCoords)) {
        return {
          isCommunity: true,
          communityId: community.id,
          communityName: community.name,
          reason: "geofence",
        };
      }
    }
  }

  // Fallback aliases even if communities fetch failed empty — BNL known name
  const n = normalize(addr);
  if (NAME_ALIASES.some((a) => n.includes(a)) || n.includes("bren northern")) {
    return {
      isCommunity: true,
      communityId: "9lSkwTx49zGTNRHlOOHb",
      communityName: "Bren Northern Lights",
      reason: "alias_fallback",
    };
  }

  return { isCommunity: false };
}

let cachedCommunities: Community[] | null = null;
let cacheAt = 0;

export async function detectCommunityOrder(args: {
  profile?: Partial<User> | null;
  address?: string | null;
  location?: { lat: number; lng: number } | null;
}): Promise<CommunityMatch> {
  const now = Date.now();
  if (!cachedCommunities || now - cacheAt > 5 * 60 * 1000) {
    try {
      cachedCommunities = await getCommunities();
      cacheAt = now;
    } catch {
      cachedCommunities = cachedCommunities || [];
    }
  }
  return matchCommunityForInputs({
    communities: cachedCommunities || [],
    ...args,
  });
}
